import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LIBELLES_STATUT,
  STATUTS_CANDIDATURE,
  auMoins,
  type AnnonceResume,
  type CandidatureResume,
} from '@rh/shared';
import { appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { ChangerStatut } from '../composants/ChangerStatut';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { ecrireParcours } from '../parcours';
import { Etoile } from '../composants/Etoile';
import { Note, Pastille } from '../composants/Pastille';
import { Signaux } from '../composants/Signaux';
import { classeNom, nomComplet } from '../format';

/** Écran 4 — suivi par statuts, défilement horizontal sur mobile (docs/UI.md). */
export function Suivi() {
  const { utilisateur } = useAuth();
  const [annonces, setAnnonces] = useState<AnnonceResume[] | null>(null);
  const [annonceId, setAnnonceId] = useState('');
  const [candidatures, setCandidatures] = useState<CandidatureResume[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    appel<AnnonceResume[]>('/annonces')
      .then((toutes) => {
        // Un brouillon n'a encore reçu aucune candidature.
        const liste = toutes.filter((annonce) => annonce.statut !== 'brouillon');
        setAnnonces(liste);
        setAnnonceId((courante) => courante || liste[0]?.id || '');
      })
      .catch((e: Error) => setErreur(e.message));
  }, []);

  const charger = useCallback(() => {
    if (!annonceId) return;
    appel<CandidatureResume[]>(`/annonces/${annonceId}/candidatures`)
      .then(setCandidatures)
      .catch((e: Error) => setErreur(e.message));
  }, [annonceId]);

  useEffect(charger, [charger]);

  // Les flèches de la fiche suivent les colonnes, de gauche à droite
  // (2026-09-19, `src/parcours.ts`).
  useEffect(() => {
    if (!candidatures) return;
    const annonce = annonces?.find((a) => a.id === annonceId);
    ecrireParcours({
      retour: '/suivi',
      libelle: annonce ? `Suivi — ${annonce.intitule}` : 'Suivi',
      ids: STATUTS_CANDIDATURE.flatMap((statut) =>
        candidatures.filter((c) => c.statut === statut).map((c) => c.id),
      ),
    });
  }, [candidatures, annonces, annonceId]);

  const rh = utilisateur ? auMoins(utilisateur.role, 'rh') : false;

  return (
    <section>
      <div className="entete-annonce">
        <h2>Suivi</h2>
        {annonces && annonces.length > 0 && (
          <select
            value={annonceId}
            aria-label="Annonce"
            onChange={(evenement) => {
              setCandidatures(null);
              setAnnonceId(evenement.target.value);
            }}
          >
            {annonces.map((annonce) => (
              <option key={annonce.id} value={annonce.id}>
                {annonce.reference} — {annonce.intitule}
              </option>
            ))}
          </select>
        )}
      </div>

      {erreur && <Erreur message={erreur} />}
      {annonces?.length === 0 && <Vide enfants="Aucune annonce." />}
      {annonceId && !candidatures && !erreur && <Chargement quoi="du suivi" />}

      {candidatures && (
        <div className="tableau-suivi">
          {STATUTS_CANDIDATURE.map((statut) => {
            const colonne = candidatures.filter((c) => c.statut === statut);
            return (
              <section key={statut} className="colonne" aria-label={LIBELLES_STATUT[statut]}>
                <h3>
                  {LIBELLES_STATUT[statut]} <span className="compte">{colonne.length}</span>
                </h3>
                {colonne.map((candidature) => (
                  <div key={candidature.id} className="carte-suivi">
                    <div className="ligne1">
                      <Pastille valeur={candidature.potentiel} libelle etiquette={candidature.etiquette} />
                      <Etoile potentiel={candidature.potentiel} />
                      <Link to={`/candidatures/${candidature.id}`} className={classeNom(candidature.statut)}>
                        {nomComplet(candidature)}
                      </Link>
                      <Note valeur={candidature.noteEntretien} date={candidature.noteLe} />
                    </div>
                    <Signaux candidature={candidature} />
                    {rh && (
                      <ChangerStatut
                        candidatureId={candidature.id}
                        statut={candidature.statut}
                        onChange={charger}
                      />
                    )}
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}
